import { useState, useCallback } from 'react';
import { useApp } from '../../components/AppProvider';
import { generateQuoteDocument } from '../../lib/pdf';
import { supabase } from '../../lib/supabase';
import { coreCall } from '../../lib/services/gatewayService';
import { useSignatureStore } from '../../lib/stores/signatureStore';
import { getUserWhatsAppConfig } from '../../lib/utils/getWhatsAppConfig';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { useDealStore } from '../../lib/stores/dealStore';
import { buildJid } from '../../lib/types/whatsappChat';
import { normalizeWhatsAppDigits } from '../../lib/services/whatsapp/phoneDigits';
import { callWahaProxy } from '../../lib/services/wahaProxyService';
import type { Deal, DealService, QuoteTemplate } from '../../lib/types';

type SendingChannel = 'signature-link' | 'email-pdf' | 'email-link' | 'whatsapp-pdf' | null;

interface UseSendProposalResult {
  sending: boolean;
  sendingChannel: SendingChannel;
  generateSignatureLink: (params: SignatureLinkParams) => Promise<string | null>;
  sendByEmailWithPDF: (params: EmailPDFParams) => Promise<boolean>;
  sendByEmailWithLink: (params: EmailLinkParams) => Promise<boolean>;
  sendByWhatsApp: (params: WhatsAppParams) => Promise<boolean>;
}

interface SignatureLinkParams {
  deal: Deal;
  template: QuoteTemplate;
  notificationEmail?: string;
  notificationPhone?: string;
}

interface EmailPDFParams {
  deal: Deal;
  services: DealService[];
  settings: any;
  template: QuoteTemplate;
  to: string;
  subject: string;
  body: string;
  /** Righe dell'offerta da includere nel PDF; assente = tutte */
  serviceIds?: string[];
}

interface EmailLinkParams {
  deal: Deal;
  services: DealService[];
  settings: any;
  template: QuoteTemplate;
  to: string;
  subject: string;
  body: string;
}

interface WhatsAppParams {
  deal: Deal;
  services: DealService[];
  settings: any;
  template: QuoteTemplate;
  phone: string;
  caption: string;
  /** Righe dell'offerta da includere nel PDF; assente = tutte */
  serviceIds?: string[];
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => {
      const base64 = reader.result?.toString().split(',')[1];
      resolve(base64 || '');
    };
    reader.onerror = reject;
  });
}

function getProposalFileName(deal: Deal): string {
  let fileName = '';

  if (deal.quote_number && typeof deal.quote_number === 'string') {
    fileName = `Preventivo_${deal.quote_number}`;
  } else {
    fileName = `Preventivo_${deal.title || 'Senza_Titolo'}`;
  }

  if (deal.client?.business_name) {
    const clientName = deal.client.business_name
      .replace(/[\\/:*?"<>|]/g, '_')
      .replace(/\s+/g, '_');
    fileName += `_${clientName}`;
  }

  const today = new Date();
  const dateStr = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
  fileName += `_${dateStr}.pdf`;

  if (fileName.length > 100) {
    fileName = fileName.substring(0, 96) + '.pdf';
  }

  return fileName;
}

async function fetchFreshDealWithClient(deal: Deal): Promise<Deal> {
  let updatedDeal = { ...deal };

  try {
    const { data: freshDealData } = await supabase
      .from('deals')
      .select('*')
      .eq('id', deal.id)
      .single();

    if (freshDealData) {
      updatedDeal = { ...freshDealData, client: deal.client };
    }
  } catch (err) {
    console.error('Error fetching fresh deal data:', err);
  }

  if (deal.client_id) {
    try {
      const { data: clientData } = await supabase
        .from('clients')
        .select(`
          *,
          sales_rep:user_profiles!clients_sales_rep_id_fkey (
            id, email, full_name, phone
          ),
          assigned_user:user_profiles!clients_assigned_to_fkey (
            id, email, full_name
          )
        `)
        .eq('id', deal.client_id)
        .single();

      if (clientData) {
        updatedDeal.client = clientData;
      }
    } catch (err) {
      console.error('Error fetching client data:', err);
    }

    // Fetch referente (persona collegata via client_relationships) per il cliente
    try {
      const { data: relRow } = await supabase
        .from('client_relationships')
        .select('role, area, person:source_client_id ( business_name, email, phone )')
        .eq('relationship_type', 'contatto')
        .eq('is_active', true)
        .eq('target_client_id', deal.client_id)
        .order('is_referente', { ascending: false })
        .order('is_primary_contact', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (relRow && (relRow as any).person) {
        const p: any = (relRow as any).person;
        (updatedDeal as any).referente = {
          name: p.business_name, phone: p.phone, email: p.email,
          role: (relRow as any).role, area: (relRow as any).area,
        };
      }
    } catch (err) {
      console.error('Error fetching referente:', err);
    }
  }

  return updatedDeal;
}

export const useSendProposal = (): UseSendProposalResult => {
  const { toast } = useApp();
  const [sending, setSending] = useState(false);
  const [sendingChannel, setSendingChannel] = useState<SendingChannel>(null);

  // Genera SOLO il link di firma online (senza inviarlo via email/WhatsApp).
  // L'utente può poi copiarlo e condividerlo come preferisce. Allinea il deal
  // a 'sent' come gli altri canali per coerenza di stato.
  const generateSignatureLink = useCallback(async (params: SignatureLinkParams): Promise<string | null> => {
    const { deal, template, notificationEmail, notificationPhone } = params;

    setSending(true);
    setSendingChannel('signature-link');

    try {
      const signatureUrlId = await useSignatureStore.getState().generateSignatureUrl(
        deal.id, deal.client_id, {
          templateId: template.id,
          notificationEmail,
          notificationPhone,
        },
      );

      if (!signatureUrlId) {
        throw new Error('Errore nella generazione del link firma');
      }

      const signatureLink = `${window.location.origin}/sign/${signatureUrlId}`;

      await useDealStore.getState().updateDeal(deal.id, {
        signature_request_url: signatureUrlId,
        status: 'sent'
      });

      toast.success('Link firma generato');
      return signatureLink;
    } catch (error) {
      console.error('Error generating signature link:', error);
      toast.error(error instanceof Error ? error.message : 'Errore nella generazione del link firma');
      return null;
    } finally {
      setSending(false);
      setSendingChannel(null);
    }
  }, [toast]);

  const sendByEmailWithPDF = useCallback(async (params: EmailPDFParams): Promise<boolean> => {
    const { deal, template, to, subject, body, serviceIds } = params;

    setSending(true);
    setSendingChannel('email-pdf');

    try {
      // Fetch fresh deal data
      const freshDeal = await fetchFreshDealWithClient(deal);

      // Generate PDF blob
      const pdfBlob = await generateQuoteDocument(freshDeal.id, template.id, 'pdf', { serviceIds });

      if (!pdfBlob || !(pdfBlob instanceof Blob)) {
        throw new Error('Errore nella generazione del PDF');
      }

      // Check size (SendGrid limit: 30MB)
      if (pdfBlob.size > 30 * 1024 * 1024) {
        throw new Error('Il PDF è troppo grande per essere inviato via email (max 30MB)');
      }

      // Convert to base64
      const base64Content = await blobToBase64(pdfBlob);
      const fileName = getProposalFileName(freshDeal);

      // Get organization ID and current user email for reply-to
      const organizationId = useSettingsStore.getState().organizationId;
      if (!organizationId) throw new Error('Organization not found');

      const { data: { user } } = await supabase.auth.getUser();
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('email')
        .eq('id', user?.id)
        .single();

      // Send via SendGrid
      const { data, error } = await coreCall('send-proposal-email', {
        body: {
          to,
          subject,
          body,
          attachment: {
            filename: fileName,
            content: base64Content,
            contentType: 'application/pdf'
          },
          organizationId,
          replyTo: userProfile?.email,
          dealId: deal.id
        }
      });

      if (error) throw new Error(error.message);
      // Wave 3.6: domain non configurato → messaggio user-friendly
      if (
        data &&
        ((data.error?.type === 'DomainNotConfigured') ||
          (typeof data.error === 'string' && data.error.toLowerCase().includes('configura il tuo dominio')))
      ) {
        toast.error('Configura il tuo dominio email in Impostazioni per inviare.');
        return false;
      }
      if (data && !data.success) throw new Error(data.error || 'Errore nell\'invio');

      // Update deal status to 'sent'
      await useDealStore.getState().updateDealStatus(deal.id, 'sent');

      toast.success('Proposta inviata via email con successo');
      return true;
    } catch (error) {
      console.error('Error sending proposal by email with PDF:', error);
      toast.error(error instanceof Error ? error.message : 'Errore nell\'invio della proposta');
      return false;
    } finally {
      setSending(false);
      setSendingChannel(null);
    }
  }, [toast]);

  const sendByEmailWithLink = useCallback(async (params: EmailLinkParams): Promise<boolean> => {
    const { deal, services, settings, template, to, subject, body } = params;

    setSending(true);
    setSendingChannel('email-link');

    try {
      // Generate signature URL
      const signatureUrlId = await useSignatureStore.getState().generateSignatureUrl(
        deal.id, deal.client_id, template.id
      );

      if (!signatureUrlId) {
        throw new Error('Errore nella generazione del link firma');
      }

      const signatureLink = `${window.location.origin}/sign/${signatureUrlId}`;

      // Replace placeholder or append link to body
      let emailBody = body;
      if (body.includes('{{LINK_FIRMA}}')) {
        emailBody = body.replace(/\{\{LINK_FIRMA\}\}/g, `<a href="${signatureLink}" style="display:inline-block;padding:12px 24px;background-color:#2563eb;color:white;text-decoration:none;border-radius:6px;font-weight:600;">Visualizza e Firma la Proposta</a>`);
      } else {
        emailBody += `<br/><br/><p>Clicca il seguente link per visualizzare e firmare la proposta:</p><p><a href="${signatureLink}" style="display:inline-block;padding:12px 24px;background-color:#2563eb;color:white;text-decoration:none;border-radius:6px;font-weight:600;">Visualizza e Firma la Proposta</a></p>`;
      }

      // Get organization ID and current user email for reply-to
      const organizationId = useSettingsStore.getState().organizationId;
      if (!organizationId) throw new Error('Organization not found');

      const { data: { user } } = await supabase.auth.getUser();
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('email')
        .eq('id', user?.id)
        .single();

      // Send via SendGrid (no attachment)
      const { data, error } = await coreCall('send-proposal-email', {
        body: {
          to,
          subject,
          body: emailBody,
          organizationId,
          replyTo: userProfile?.email,
          dealId: deal.id
        }
      });

      if (error) throw new Error(error.message);
      // Wave 3.6: domain non configurato → messaggio user-friendly
      if (
        data &&
        ((data.error?.type === 'DomainNotConfigured') ||
          (typeof data.error === 'string' && data.error.toLowerCase().includes('configura il tuo dominio')))
      ) {
        toast.error('Configura il tuo dominio email in Impostazioni per inviare.');
        return false;
      }
      if (data && !data.success) throw new Error(data.error || 'Errore nell\'invio');

      // Update deal with signature URL and status
      await useDealStore.getState().updateDeal(deal.id, {
        signature_request_url: signatureUrlId,
        status: 'sent'
      });

      toast.success('Link firma inviato via email con successo');
      return true;
    } catch (error) {
      console.error('Error sending proposal by email with link:', error);
      toast.error(error instanceof Error ? error.message : 'Errore nell\'invio del link firma');
      return false;
    } finally {
      setSending(false);
      setSendingChannel(null);
    }
  }, [toast]);

  const sendByWhatsApp = useCallback(async (params: WhatsAppParams): Promise<boolean> => {
    const { deal, template, phone, caption, serviceIds } = params;

    setSending(true);
    setSendingChannel('whatsapp-pdf');

    try {
      // Get WhatsApp config
      const { config, organizationId } = await getUserWhatsAppConfig();

      // Fetch fresh deal data
      const freshDeal = await fetchFreshDealWithClient(deal);

      // Generate PDF blob
      const pdfBlob = await generateQuoteDocument(freshDeal.id, template.id, 'pdf', { serviceIds });

      if (!pdfBlob || !(pdfBlob instanceof Blob)) {
        throw new Error('Errore nella generazione del PDF');
      }

      const fileName = getProposalFileName(freshDeal);

      // Upload to Supabase Storage
      const timestamp = Date.now();
      const bucketPath = `whatsapp_media/${organizationId}/proposals/${deal.id}/${timestamp}_${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('media')
        .upload(bucketPath, pdfBlob, {
          cacheControl: '3600',
          upsert: true,
          contentType: 'application/pdf'
        });

      if (uploadError) {
        throw new Error(`Errore nel caricamento del PDF: ${uploadError.message}`);
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('media')
        .getPublicUrl(bucketPath);

      // Build chat ID from phone: stessa normalizzazione delle chat (39 ai numeri italiani
      // salvati senza prefisso). Prima un 333… diventava 333…@c.us e il PDF non partiva.
      const chatId = buildJid(normalizeWhatsAppDigits(phone));

      // Send via WAHA (proxy)
      const response = await callWahaProxy({
        method: 'POST',
        path: '/api/sendFile',
        body: {
          session: config.configuration.session,
          chatId,
          file: {
            url: publicUrl,
            mimetype: 'application/pdf',
            filename: fileName
          },
          caption: caption || undefined
        },
        signal: AbortSignal.timeout(30000),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Errore invio WhatsApp: ${response.status} ${errorText}`);
      }

      // Update deal status to 'sent'
      await useDealStore.getState().updateDealStatus(deal.id, 'sent');

      toast.success('Proposta inviata via WhatsApp con successo');
      return true;
    } catch (error) {
      console.error('Error sending proposal by WhatsApp:', error);
      toast.error(error instanceof Error ? error.message : 'Errore nell\'invio su WhatsApp');
      return false;
    } finally {
      setSending(false);
      setSendingChannel(null);
    }
  }, [toast]);

  return {
    sending,
    sendingChannel,
    generateSignatureLink,
    sendByEmailWithPDF,
    sendByEmailWithLink,
    sendByWhatsApp
  };
};
